import * as React from "react";
import { Trans, useTranslation } from "react-i18next";
import styled from "styled-components";
import { locales } from "../utils/locale";
import { SelectField } from "./FieldRenderers";
import { PrimaryGradient } from "./Utils";

const FieldWrap = styled.div`
  width: 100%;
  padding: ${(props) => props.theme.space[4]} 0;
  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    width: 25%;
  }
  &&& > label > span {
    color: ${(props) => props.theme.colors.offBlack};
    font-size: 1.1rem;
  }
`;

const StyledFooterOuter = styled.footer`
  ${PrimaryGradient}
`;

const StyledFooterInner = styled.div`
  max-width: 1220px;
  margin: 0 auto;
  padding: 0 ${(props) => props.theme.space[4]};
`;

const FooterContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 1.5rem 0 0;
`;

const FooterLinkWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 0.5rem;
  width: 100%;
  margin: 2rem 0;
  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    flex-wrap: nowrap;
    width: 60%;
  }
`;

const FooterLink = styled.a`
  color: ${(props) => props.theme.colors.offBlack};
  font-family: inherit;
  font-size: 1.1rem;
  font-weight: 400;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const FooterVerticalDivider = styled.div`
  border-left: 1px solid ${(props) => props.theme.colors.offBlack};
  margin: 0 1.3rem;
`;

const FooterTextHeavy = styled.span`
  color: ${(props) => props.theme.colors.offBlack};
  font-size: 1.1rem;
  font-weight: 500;
`;

const FooterText = styled.span`
  color: ${(props) => props.theme.colors.offBlack};
  font-size: 1.1rem;
  font-weight: 400;
`;

interface IProps {}

const handleFilterChange = (url: string) => {
  window.location.href = url;
};

const Footer: React.FC<IProps> = () => {
  const { t } = useTranslation();

  const localeByCode = Object.fromEntries(
    locales.map((l) => [l.countryCode, l]),
  );

  return (
    <>
      <StyledFooterOuter>
        <StyledFooterInner>
          <FooterContent>
            <FooterTextHeavy>
              <>&#169;</>
              {t(
                "footer.copyrightn",
                "{{year}} NBA Media Ventures, LLC. All rights reserved.",
                { year: new Date().getFullYear() },
              )}
            </FooterTextHeavy>
            <FooterLinkWrapper>
              <FooterLink
                href="https://www.nba.com/privacy-policy"
                target="_blank"
              >
                {t("footer.privacy", "Privacy Policy")}
              </FooterLink>
              <FooterVerticalDivider />
              <FooterLink href="https://www.nba.com/termsofuse" target="_blank">
                {t("footer.terms", "Terms of Use")}
              </FooterLink>
              <FooterVerticalDivider />
              <FooterLink
                href="https://www.nba.com/accessibility"
                target="_blank"
              >
                {t("footer.a11y", "Accessibility and Closed Captions")}
              </FooterLink>
              <FooterVerticalDivider />
              <FooterLink
                href="https://privacyportal.onetrust.com/webform/4b9fe937-73ed-43ed-863a-241ac6bfb482/784f7dd4-5b8b-4569-8087-4560abeab57e"
                target="_blank"
              >
                {t("footer.personal", "Do Not Sell My Personal Information")}
              </FooterLink>
              <FooterVerticalDivider />
              <FooterLink
                href="https://support.watch.nba.com/hc/en-us"
                target="_blank"
              >
                {t("footer.support", "Customer Support")}
              </FooterLink>
            </FooterLinkWrapper>
            <FooterText>
              <Trans i18nKey="footer.a11y2">
                If you are having difficulty accessing any content on this
                website, please visit our{" "}
                <FooterLink
                  href="https://www.nba.com/accessibility"
                  target="_blank"
                >
                  Accessibility page.
                </FooterLink>
              </Trans>
            </FooterText>
          </FooterContent>
          <form>
            <FieldWrap>
              <SelectField
                id="filter"
                name="filter"
                label={t("footer.pickCountry", "Pick your country/ language")}
                value={window.location.origin}
                onChange={(e) => handleFilterChange(e.target.value)}
                required={false}
              >
                <option
                  key="gb"
                  value={localeByCode.gb.url}
                  aria-selected={window.location.origin === localeByCode.gb.url}
                >
                  {t("footer.gb", "English (GB)")}
                </option>
                <option
                  key="us"
                  value={localeByCode.us.url}
                  aria-selected={window.location.origin === localeByCode.us.url}
                >
                  {t("footer.us", "English (US)")}
                </option>
                <option
                  key="de"
                  value={localeByCode.de.url}
                  aria-selected={window.location.origin === localeByCode.de.url}
                >
                  {t("footer.de", "German")}
                </option>
                <option
                  key="fr"
                  value={localeByCode.fr.url}
                  aria-selected={window.location.origin === localeByCode.fr.url}
                >
                  {t("footer.fr", "French")}
                </option>
                <option
                  key="es"
                  value={localeByCode.es.url}
                  aria-selected={window.location.origin === localeByCode.es.url}
                >
                  {t("footer.es", "Spanish")}
                </option>
                <option
                  key="it"
                  value={localeByCode.it.url}
                  aria-selected={window.location.origin === localeByCode.it.url}
                >
                  {t("footer.it", "Italian")}
                </option>
                <option
                  key="gr"
                  value={localeByCode.gr.url}
                  aria-selected={window.location.origin === localeByCode.gr.url}
                >
                  {t("footer.gr", "Greek")}
                </option>
                <option
                  key="tr"
                  value={localeByCode.tr.url}
                  aria-selected={window.location.origin === localeByCode.tr.url}
                >
                  {t("footer.tr", "Turkish")}
                </option>
                <option
                  key="he"
                  value={localeByCode.he.url}
                  aria-selected={window.location.origin === localeByCode.he.url}
                >
                  {t("footer.he", "Hebrew")}
                </option>
                <option
                  key="br"
                  value={localeByCode.br.url}
                  aria-selected={window.location.origin === localeByCode.br.url}
                >
                  {t("footer.br", "Portuguese (BR)")}
                </option>
              </SelectField>
            </FieldWrap>
          </form>
        </StyledFooterInner>
      </StyledFooterOuter>
    </>
  );
};

export default Footer;
